import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CloudOff, MapPin, Play, RefreshCw, ScanLine, Square, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import jobService from '../../services/jobs';
import fieldWorkService from '../../services/fieldWork';
import labourService from '../../services/labour';
import {
  enqueueCrewAction,
  flushCrewQueue,
  pendingCrewActions,
  subscribeCrewQueue,
} from '../../services/crewActionQueue';
import { errorMessage } from '../../utils/apiResponse';
import { workerDisplayName, type Crew, type Worker } from '../../types/fieldWork';
import type { CostCentre, CrewAction, OpenSegment, PlaceTarget } from '../../types/labour';
import type { Job } from '../../types/jobs';

/** A place people can be working: one of the job's targets, or a cost centre. */
interface Place {
  key: string;
  label: string;
  target: PlaceTarget | null;
  cost_centre_id: number | null;
}

function elapsed(fromIso: string, now: number): string {
  const mins = Math.max(0, Math.floor((now - new Date(fromIso).getTime()) / 60000));
  const h = Math.floor(mins / 60);
  return h ? `${h}h ${String(mins % 60).padStart(2, '0')}m` : `${mins}m`;
}

/**
 * /jobs/:id/crew — a supervisor runs a crew on a job from one phone: start
 * people (scan their cards, add a whole crew, or tick names), move them to
 * another block, stop them. Pickers need no app. Every tap is queued with its
 * own time before it is sent, so a dead spot in the orchard loses nothing.
 */
export default function CrewMode() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const navigate = useNavigate();

  const [job, setJob] = useState<Job | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [open, setOpen] = useState<OpenSegment[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [placeKey, setPlaceKey] = useState<string>('');
  const [selectedOpen, setSelectedOpen] = useState<Set<number>>(new Set());
  const [toStart, setToStart] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [pending, setPending] = useState(() => pendingCrewActions(jobId).length);
  const [offline, setOffline] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => subscribeCrewQueue(() => setPending(pendingCrewActions(jobId).length)), [jobId]);

  const sync = useCallback(async () => {
    try {
      const r = await flushCrewQueue(jobId);
      setOffline(r.offline);
      if (r.open) setOpen(r.open);
      if (r.rejected.length) setProblems(p => [...p, ...r.rejected.map(x => x.error || 'An action was refused')]);
    } catch (e) {
      setProblems(p => [...p, errorMessage(e, 'Could not send to the server')]);
    }
  }, [jobId]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [j, w, c, cc] = await Promise.all([
          jobService.getJob(jobId),
          fieldWorkService.getWorkers(),
          fieldWorkService.getCrews(),
          labourService.getCostCentres(),
        ]);
        if (!live) return;
        if (j.success) setJob(j.data);
        setWorkers(w);
        setCrews(c);
        setCostCentres(cc);
      } catch (e) {
        if (live) setLoadError(errorMessage(e, 'Could not load this job'));
      } finally {
        if (live) setLoading(false);
      }
      if (live) await sync();
    })();
    const onOnline = () => { sync(); };
    window.addEventListener('online', onOnline);
    return () => { live = false; window.removeEventListener('online', onOnline); };
  }, [jobId, sync]);

  const places: Place[] = useMemo(() => {
    const list: Place[] = (job?.targets ?? []).map(t => ({
      key: `${t.target_type}:${t.target_id}`,
      label: t.target_name || `${t.target_type} ${t.target_id}`,
      target: { type: t.target_type, id: t.target_id },
      cost_centre_id: null,
    }));
    for (const c of costCentres) {
      list.push({ key: `cc:${c.id}`, label: c.name, target: null, cost_centre_id: c.id });
    }
    return list;
  }, [job, costCentres]);

  // Default to the job's only target; with several, make the supervisor choose.
  useEffect(() => {
    if (!placeKey && job && (job.targets?.length ?? 0) === 1 && places.length) setPlaceKey(places[0].key);
  }, [job, places, placeKey]);

  const place = places.find(p => p.key === placeKey) ?? null;
  const needsPlace = (job?.targets?.length ?? 0) > 1 && !place;

  const onJob = useMemo(() => new Set(open.map(o => o.worker_id)), [open]);
  const available = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return workers.filter(w => !onJob.has(w.id)
      && (!q || `${workerDisplayName(w)} ${w.picker_code}`.toLowerCase().includes(q)));
  }, [workers, onJob, filter]);

  const grouped = useMemo(() => {
    const m = new Map<string, OpenSegment[]>();
    for (const o of open) {
      const k = o.place_name || job?.target_name || 'On the job';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(o);
    }
    return [...m.entries()];
  }, [open, job]);

  /** Apply an action to what is on screen straight away, then send it. */
  const act = async (a: Omit<CrewAction, 'client_uuid' | 'at'>) => {
    const queued = enqueueCrewAction(jobId, a);
    const at = queued.at as string;
    setOpen(cur => {
      const ids = new Set(a.worker_ids?.length ? a.worker_ids : cur.map(o => o.worker_id));
      if (a.action === 'stop') return cur.filter(o => !ids.has(o.worker_id));
      const label = place?.label ?? null;
      const moved = cur.filter(o => ids.has(o.worker_id)).map(o => ({
        ...o, started_at: at, place_name: label, cost_centre_id: a.cost_centre_id ?? null,
        target_type: a.target?.type ?? null, target_id: a.target?.id ?? null,
      }));
      const added: OpenSegment[] = a.action === 'start'
        ? workers.filter(w => ids.has(w.id) && !cur.some(o => o.worker_id === w.id)).map(w => ({
          id: -w.id, worker_id: w.id, started_at: at, source: 'crew',
          target_type: a.target?.type ?? null, target_id: a.target?.id ?? null,
          cost_centre_id: a.cost_centre_id ?? null, farm_id: null, block_id: null, cultivar_area_id: null,
          first_name: w.first_name, last_name: w.last_name, preferred_name: w.preferred_name,
          picker_code: w.picker_code, worker_type: w.worker_type, place_name: label,
        }))
        : [];
      return [...cur.filter(o => !ids.has(o.worker_id)), ...moved, ...added];
    });
    await sync();
  };

  const placePayload = () => ({ target: place?.target ?? null, cost_centre_id: place?.cost_centre_id ?? null });

  const startSelected = async () => {
    const ids = [...toStart];
    setToStart(new Set());
    await act({ action: 'start', worker_ids: ids, ...placePayload() });
  };

  const onScan = async (code: string) => {
    const c = code.trim().toUpperCase();
    const w = workers.find(x => x.picker_code.toUpperCase() === c);
    if (!w) { setScanNote(`${c}: not on the roster`); return; }
    if (onJob.has(w.id)) { setScanNote(`${workerDisplayName(w)} is already on this job`); return; }
    setToStart(s => new Set(s).add(w.id));
    setScanNote(`Added ${workerDisplayName(w)}`);
  };

  const addCrew = (crewId: number) => {
    const crew = crews.find(c => c.id === crewId);
    if (!crew) return;
    setToStart(s => {
      const n = new Set(s);
      for (const m of crew.members) if (!onJob.has(m.worker_id)) n.add(m.worker_id);
      return n;
    });
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-center text-sm text-neutral-500">Loading…</div>;
  }
  if (!job) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-neutral-500">{loadError || 'Job not found'}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/jobs')}>Back to Jobs</Button>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Crew mode" subtitle={job.name} icon={<Users className="h-6 w-6 text-white" />} />
      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <button onClick={() => navigate(`/jobs/${jobId}`)} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
          <ArrowLeft className="h-4 w-4" /> Back to job
        </button>

        {(pending > 0 || offline) && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <CloudOff className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 min-w-0">
              {offline ? 'No signal. ' : ''}{pending} {pending === 1 ? 'change' : 'changes'} waiting to send — times are kept as tapped.
            </span>
            <Button size="sm" variant="outline" onClick={sync}><RefreshCw className="h-4 w-4" /> Send now</Button>
          </div>
        )}
        {problems.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 space-y-1">
            {problems.map((p, i) => <p key={i}>{p}</p>)}
            <button className="text-xs underline" onClick={() => setProblems([])}>Dismiss</button>
          </div>
        )}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2">
          <label htmlFor="crew-place" className="flex items-center gap-2 text-sm font-medium text-neutral-700">
            <MapPin className="h-4 w-4 text-indigo-600" /> Working in
          </label>
          <select id="crew-place" value={placeKey} onChange={e => setPlaceKey(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-200">
            {(job.targets?.length ?? 0) !== 1 && (
              <option value="">{(job.targets?.length ?? 0) > 1 ? 'Choose a block…' : 'The job’s area'}</option>
            )}
            {places.filter(p => p.target).length > 0 && (
              <optgroup label="Job areas">
                {places.filter(p => p.target).map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </optgroup>
            )}
            {places.filter(p => p.cost_centre_id).length > 0 && (
              <optgroup label="Not in a block">
                {places.filter(p => p.cost_centre_id).map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </optgroup>
            )}
          </select>
        </section>

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="On the job">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-neutral-900">On the job ({open.length})</h2>
            {open.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={needsPlace || !place}
                  onClick={() => act({ action: 'switch', worker_ids: [...selectedOpen], ...placePayload() }).then(() => setSelectedOpen(new Set()))}>
                  <MapPin className="h-4 w-4" /> Move {selectedOpen.size ? selectedOpen.size : 'all'} here
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => act({ action: 'stop', worker_ids: [...selectedOpen] }).then(() => setSelectedOpen(new Set()))}>
                  <Square className="h-4 w-4" /> Stop {selectedOpen.size ? selectedOpen.size : 'everyone'}
                </Button>
              </div>
            )}
          </div>
          {open.length === 0 ? (
            <p className="text-sm text-neutral-500">Nobody is on this job yet. Add people below.</p>
          ) : grouped.map(([placeName, rows]) => (
            <div key={placeName}>
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">{placeName}</p>
              <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
                {rows.map(o => (
                  <li key={o.worker_id}>
                    <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={selectedOpen.has(o.worker_id)}
                        onChange={() => setSelectedOpen(s => { const n = new Set(s); if (n.has(o.worker_id)) n.delete(o.worker_id); else n.add(o.worker_id); return n; })}
                        className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
                      <span className="flex-1 min-w-0 truncate text-neutral-900">{workerDisplayName(o)}</span>
                      <span className="font-mono text-xs text-neutral-500">{o.picker_code}</span>
                      <span className="text-xs text-neutral-600 w-14 text-right">{elapsed(o.started_at, now)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="Add people">
          <h2 className="font-semibold text-neutral-900">Add people</h2>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => { setScanNote(null); setScanning(true); }}>
              <ScanLine className="h-4 w-4" /> Scan cards
            </Button>
            {crews.length > 0 && (
              <select aria-label="Add a crew" value="" onChange={e => e.target.value && addCrew(Number(e.target.value))}
                className="min-w-0 max-w-full flex-1 border border-neutral-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                <option value="">Add a crew…</option>
                {crews.map(c => <option key={c.id} value={c.id}>{c.name} ({c.members.length})</option>)}
              </select>
            )}
          </div>
          <input aria-label="Find a person" placeholder="Find by name or code" value={filter} onChange={e => setFilter(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200" />
          <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg max-h-72 overflow-y-auto">
            {available.map(w => (
              <li key={w.id}>
                <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={toStart.has(w.id)}
                    onChange={() => setToStart(s => { const n = new Set(s); if (n.has(w.id)) n.delete(w.id); else n.add(w.id); return n; })}
                    className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
                  <span className="flex-1 min-w-0 truncate text-neutral-900">{workerDisplayName(w)}</span>
                  <span className="font-mono text-xs text-neutral-500">{w.picker_code}</span>
                </label>
              </li>
            ))}
            {available.length === 0 && <li className="px-3 py-3 text-sm text-neutral-500">Nobody else to add.</li>}
          </ul>
          <Button variant="primary" className="w-full" disabled={toStart.size === 0 || needsPlace} onClick={startSelected}>
            <Play className="h-4 w-4" />
            {needsPlace ? 'Choose where they are working' : `Start ${toStart.size || ''} ${place ? `in ${place.label}` : 'on this job'}`.replace(/\s+/g, ' ')}
          </Button>
        </section>
      </div>

      {scanning && (
        <BarcodeScanner
          label={scanNote ? `Scan picker cards — ${scanNote}` : 'Scan picker cards'}
          onScan={onScan}
          onClose={() => setScanning(false)}
        />
      )}
    </>
  );
}
